import React from 'react'
import { useDispatch } from 'react-redux'
import { store } from '@core/Store'
import {
  ACCOUNT_ACTIVITY_PENDING_ROUTE,
  ACCOUNT_ACTIVITY_RESULTS_ROUTE,
} from '@core/Areas/AccountActivity/constants'
import {
  KAMBI_PENDING_HASH_ROUTE,
  KAMBI_RESULTED_HASH_ROUTE,
  KAMBI_ROOT_ROUTE,
} from '@core/Areas/KambiClient/constants'
import { getWapi } from '@core/Areas/KambiClient/helpers'
import { appHistory } from '@core/Utils/routing/history'
import {
  setBetSlipIsOpen,
  setIsPreviousPendingBetsSportsOnly,
  setShouldAllowPendingRacingTicketCountPolling,
} from '@core/Areas/AppHeader/Store'
import { selectIsPreviousPendingBetsSportsOnly } from '@core/Areas/AppHeader/Store/selectors'
import { PendingTicketsCountProvider } from '@core/Data/Account/PendingTicketsCount/PendingTicketsCountProvider'
import { trackPendingBetsButtonClick } from '../../analytics'
import { Button } from '../Common/Button'
import type { DisplayLocation } from '../../types'
import { useInvalidatePendingCountOnEvents } from './hooks'

export const PendingBetsButton = React.memo<{ displayLocation: DisplayLocation }>(
  ({ displayLocation }) => {
    const dispatch = useDispatch()

    useInvalidatePendingCountOnEvents()

    // Reset polling state on unmount
    React.useEffect(() => {
      return () => {
        dispatch(setShouldAllowPendingRacingTicketCountPolling(true))
      }
    }, [dispatch])

    return (
      <PendingTicketsCountProvider
        render={({
          pendingBetsCount,
          pendingSportsCount,
          pendingRacingCount,
          isRacingLoading,
          isRacingError,
          isKambiLoading,
          isKambiError,
        }) => {
          const handleClick = () => {
            const isSportsOnlyPending = !!pendingSportsCount && pendingRacingCount === 0
            const myBetsRoute = getMyBetsRoute({
              pendingBetsCount,
              isSportsOnlyPending,
              displayLocation,
            })
            appHistory.push(myBetsRoute)
            trackPendingBetsButtonClick(displayLocation)
            if (displayLocation === 'betslip') {
              dispatch(setBetSlipIsOpen(false))
            }
            if (myBetsRoute.includes(KAMBI_ROOT_ROUTE)) {
              getWapi().then(wapi => wapi.navigateClient(myBetsRoute))
            }
          }

          if (isKambiError && isRacingError) return null

          return (
            <Button
              type='pending'
              displayLocation={displayLocation}
              count={isRacingLoading || isKambiLoading ? null : pendingBetsCount}
              onClick={handleClick}
            />
          )
        }}
      />
    )
  }
)

PendingBetsButton.displayName = 'PendingBetsButton'

// =============
// Local Helpers
// =============

function getMyBetsRoute({
  pendingBetsCount,
  isSportsOnlyPending,
  displayLocation,
}: {
  isSportsOnlyPending: boolean
  pendingBetsCount: number | null
  displayLocation: DisplayLocation
}): `/${string}` {
  if (pendingBetsCount === null) return `/${ACCOUNT_ACTIVITY_PENDING_ROUTE}`

  const isPreviousPendingSportsOnly = selectIsPreviousPendingBetsSportsOnly(store.getState())

  if (pendingBetsCount === 0) {
    return isPreviousPendingSportsOnly
      ? KAMBI_RESULTED_HASH_ROUTE
      : `/${ACCOUNT_ACTIVITY_RESULTS_ROUTE}`
  }

  if (isSportsOnlyPending && displayLocation !== 'betslip') {
    store.dispatch(setIsPreviousPendingBetsSportsOnly(true))
    return KAMBI_PENDING_HASH_ROUTE
  }

  store.dispatch(setIsPreviousPendingBetsSportsOnly(false))
  return `/${ACCOUNT_ACTIVITY_PENDING_ROUTE}`
}
